import { isWithinStay, type PlaceEvent, type ScaleEvent, type SpecialEvent } from '../domain/scales'

type TimelineEntryKind = 'ARRIVAL' | 'DEPARTURE' | 'START' | 'END'

interface TimelineEntry<E extends ScaleEvent> {
  kind: TimelineEntryKind
  time: string
  event: E
  number: number | null
}

export interface TimelineColumn<E extends ScaleEvent> {
  stay: (E & PlaceEvent) | null
  entries: TimelineEntry<E>[]
}

const byStart = (a: ScaleEvent, b: ScaleEvent): number => Date.parse(a.startTime) - Date.parse(b.startTime) || a.id.localeCompare(b.id)

const KIND_ORDER: Record<TimelineEntryKind, number> = { ARRIVAL: 0, START: 1, END: 2, DEPARTURE: 3 }

const byTime = <E extends ScaleEvent>(a: TimelineEntry<E>, b: TimelineEntry<E>): number =>
  Date.parse(a.time) - Date.parse(b.time) || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]

const arrivalFirst = <E extends ScaleEvent>(a: TimelineEntry<E>, b: TimelineEntry<E>): number =>
  (a.kind === 'ARRIVAL' ? 0 : 1) - (b.kind === 'ARRIVAL' ? 0 : 1) || byTime(a, b)

export const scaleTimeline = <E extends ScaleEvent>(
  events: readonly E[],
  stayOf: (event: E) => string | null = () => null,
): TimelineColumn<E>[] => {
  const stays = events.filter((e): e is E & PlaceEvent => e.type === 'PLACE').sort(byStart)
  const specials = events.filter((e): e is E & SpecialEvent => e.type !== 'PLACE').sort(byStart)

  const numbers = new Map<string, number>()
  const counts: Partial<Record<SpecialEvent['type'], number>> = {}
  for (const event of specials) {
    const number = (counts[event.type] ?? 0) + 1
    counts[event.type] = number
    numbers.set(event.id, number)
  }

  const stayColumns = new Map<string, TimelineEntry<E>[]>(stays.map(stay => [stay.id, []]))
  for (const stay of stays) {
    const entries = stayColumns.get(stay.id) ?? []
    entries.push({ kind: 'ARRIVAL', time: stay.startTime, event: stay, number: null })
    if (stay.endTime !== null) entries.push({ kind: 'DEPARTURE', time: stay.endTime, event: stay, number: null })
  }

  const columns: (TimelineColumn<E> & { at: number })[] = stays.map(stay => ({
    stay,
    entries: stayColumns.get(stay.id) ?? [],
    at: Date.parse(stay.startTime),
  }))
  for (const event of specials) {
    const number = numbers.get(event.id) ?? null
    const marks: TimelineEntry<E>[] = [{ kind: 'START', time: event.startTime, event, number }]
    if (event.endTime !== null) marks.push({ kind: 'END', time: event.endTime, event, number })
    const start = Date.parse(event.startTime)
    const home = stayOf(event) ?? [...stays].reverse().find(stay => isWithinStay(stay, start))?.id
    const entries = home === undefined ? undefined : stayColumns.get(home)
    if (entries) entries.push(...marks)
    else columns.push({ stay: null, entries: marks, at: start })
  }

  const ordered = columns.sort((a, b) => a.at - b.at || (a.stay ? 0 : 1) - (b.stay ? 0 : 1))
  const merged: TimelineColumn<E>[] = []
  for (const { stay, entries } of ordered) {
    const previous = merged.at(-1)
    if (stay === null && previous?.stay === null) previous.entries.push(...entries)
    else merged.push({ stay, entries })
  }
  for (const column of merged) column.entries.sort(arrivalFirst)
  return merged
}
